/**
 * A sessão viva — tokens em MEMÓRIA, e a renovação que os mantém úteis.
 *
 * **Nada aqui é persistido, e é esse o ponto.** Até esta entrega, access
 * e refresh token moravam no `localStorage` (pendência 5 da lista de
 * segurança): um XSS lia os dois, os levava embora e a sessão continuava
 * valendo fora do navegador da vítima, por dias. Em memória, o mesmo XSS
 * ainda faz estrago enquanto a página está aberta — o que ele não faz
 * mais é EXFILTRAR uma sessão que sobrevive ao fechamento da aba.
 *
 * O preço é que recarregar a página perde os tokens. Quem paga esse
 * preço é o `prompt=none` do `oidc.ts`, contra o cookie de SSO do
 * Keycloak, que é `HttpOnly` e portanto fora do alcance de script.
 *
 * **Duas renovações, e as duas são necessárias.** A programada cobre o
 * caso normal: um temporizador dispara pouco antes do vencimento e a
 * pessoa nunca vê nada. A reativa (no 401) cobre o caso que o
 * temporizador não tem como cobrir — aba adormecida, máquina suspensa,
 * relógio que andou —, em que o token vence sem o `setTimeout` ter
 * corrido. Só a primeira deixaria um "deu erro" na volta do almoço.
 *
 * **A renovação é deduplicada.** Cinco telas pedindo dado ao mesmo tempo
 * tomam 401 juntas; sem a promessa compartilhada, seriam cinco trocas do
 * mesmo refresh token — e com rotação ligada no realm (está), a primeira
 * invalida as outras quatro e a sessão cai. O bug apareceria como
 * "desloga sozinho quando abro várias abas".
 */

import { type TokenSet, refreshTokens } from "@/shared/auth/oidc";

type Listener = (tokens: TokenSet | null) => void;

let current: TokenSet | null = null;
let timer: ReturnType<typeof setTimeout> | null = null;
let inFlight: Promise<TokenSet | null> | null = null;
const listeners = new Set<Listener>();

/** Avisado quando a sessão morre de vez (refresh recusado). Quem escuta
 *  é a casca, que manda pro login. */
let onLost: (() => void) | null = null;

export function subscribeToSession(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function onSessionLost(handler: () => void): void {
  onLost = handler;
}

function announce(): void {
  for (const listener of listeners) listener(current);
}

export function currentTokens(): TokenSet | null {
  return current;
}

export function currentAccessToken(): string | null {
  return current?.accessToken ?? null;
}

export function setSession(tokens: TokenSet): void {
  current = tokens;
  scheduleRenewal();
  announce();
}

export function clearSession(): void {
  current = null;
  if (timer) clearTimeout(timer);
  timer = null;
  inFlight = null;
  announce();
}

/**
 * Agenda a renovação para pouco antes do vencimento.
 *
 * O piso de 5s existe pra um token já vencido (ou quase) não virar um
 * laço de renovação girando a milhares por segundo — que é o que um
 * `setTimeout` com atraso negativo produz.
 */
function scheduleRenewal(): void {
  if (timer) clearTimeout(timer);
  timer = null;
  if (!current?.refreshToken) return;
  const delay = Math.max(current.expiresAt - Date.now(), 5_000);
  timer = setTimeout(() => void renew(), delay);
}

/**
 * Troca o refresh token por um par novo. Devolve `null` quando a sessão
 * acabou de verdade — e nesse caso já avisou quem escuta.
 *
 * A promessa fica compartilhada durante o voo: é o que impede N
 * chamadas simultâneas de gastarem N refresh tokens (ver o cabeçalho).
 */
export function renew(): Promise<TokenSet | null> {
  if (inFlight) return inFlight;
  const refreshToken = current?.refreshToken;
  if (!refreshToken) {
    loseSession();
    return Promise.resolve(null);
  }

  inFlight = refreshTokens(refreshToken)
    .then(tokens => {
      current = tokens;
      scheduleRenewal();
      announce();
      return tokens;
    })
    .catch(() => {
      // Refresh recusado é sessão encerrada: expirou, foi revogada no
      // console, ou o realm reiniciou. Não há o que tentar de novo.
      loseSession();
      return null;
    })
    .finally(() => {
      inFlight = null;
    });

  return inFlight;
}

function loseSession(): void {
  const tinha = current !== null;
  clearSession();
  if (tinha && onLost) onLost();
}

/**
 * O token a usar agora, renovando antes se ele estiver por vencer.
 *
 * Chamado pelo caminho reativo do `http.ts`; o proativo é o
 * temporizador. Devolve `null` quando não há sessão.
 */
export async function freshAccessToken(): Promise<string | null> {
  if (!current) return null;
  if (Date.now() < current.expiresAt) return current.accessToken;
  const renewed = await renew();
  return renewed?.accessToken ?? null;
}
